import { defineAction } from '~/prix'
import { z } from 'zod'
import expression from '~/prix/types/expression'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { citizenHealthProfileShape } from '../../citizenHealthProfile/stats/citizenHealthProfileStats.definition'
import { citizenShapeBase, citizenLocationShape } from '../../citizen/stats/citizenShape'
import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForCurrentOnly } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import healthTeamStatsAction from './healthTeamStats.action.json' with { type: 'json' }
import { dateOrIsoString } from '~/prix/types/date'
import prefixProperties from '~/prix/utils/prefixProperties'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'

export const healthTeamShape = z.object({
  originalId: z.string().describe('ID original da equipe'),
  dataSourceId: z.number().describe('ID da fonte de dados'),
  ine: z.string().describe('INE da equipe').nullish(),
  healthUnitCnes: z.string().describe('CNES da unidade de saúde').nullish(),
  isActive: z.boolean().describe('Equipe ativa').nullish(),
  teamName: z.string().describe('Nome da equipe').nullish(),
  healthTeamType: z.string().describe('Tipo da equipe de saúde').nullish(),
  professionalIne: z.string().describe('INE do profissional').nullish(),
  isDeleted: z.boolean().describe('Equipe deletada').nullish(),
  ...prefixProperties('citizen.', citizenShapeBase.shape),
  ...prefixProperties('citizen.', citizenLocationShape.shape),
  ...prefixProperties('citizenHealth.', citizenHealthProfileShape.shape),
})

const healthTeamExpression = expression(healthTeamShape.shape)

export const healthTeamVariable: Variable = {
  key: 'healthTeam',
  categoryKey: 'healthTeam',
  actionDefinitionKey: healthTeamStatsAction.key,
  title: 'Cadastros individuais por equipe de saúde',
  unitSingular: 'cadastro individual',
  unitPlural: 'cadastros individuais',
  grammaticalGender: 'male',
  timeType: 'currentOnly',
  shape: getZodSchemaInfo(healthTeamShape),
  filterableBy: getZodSchemaInfo(
    healthTeamShape.pick({
     /* CIDADÃO */
         'citizen.age': true,
         'citizen.sex': true,
         'citizen.deceased': true,
         /* SAÚDE */
         'citizenHealth.hasHypertension': true,
         'citizenHealth.hasDiabetes': true,
         'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions': true,
         'citizenHealth.isPregnant': true,
         'citizenHealth.smoker': true,
         'citizenHealth.skinColorOrRace': true,
         'citizenHealth.educationLevel': true,
         'citizenHealth.employmentStatus': true,
         'citizenHealth.homeless': true,
         'citizenHealth.hasPrivateHealthInsurance': true,
         'citizenHealth.nationality': true,
    })
  ),
  betterDensity: 'less',
  relevance: healthVariableBaseRelevance.healthTeam,
  basicSelect: ['citizen.name', 'citizen.socialName'],
  detailedSelect: [
     /* CIDADÃO */
    'citizen.originalId',
    'citizen.sex',
    'citizen.birthDate',
    'citizen.phoneNumber',
    'citizen.cns',
    'citizen.cpf',
    'citizen.streetName',
    'citizen.streetNumber',
    'citizen.neighborhood',
    'citizen.complement',
    'citizen.referencePoint',
    'citizen.postalCode',
    'citizen.deceased',
    'citizen.latitude',
    'citizen.longitude',
    /* SAÚDE */
    'citizenHealth.email',
    'citizenHealth.isResponsibleFamilyMember',
    'citizenHealth.hasDiabetes',
    'citizenHealth.hasHypertension',
    'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions',
    'citizenHealth.isPregnant',
    'citizenHealth.smoker',
    'citizenHealth.skinColorOrRace',
    'citizenHealth.educationLevel',
    'citizenHealth.employmentStatus',
    'citizenHealth.homeless',
    'citizenHealth.hasPrivateHealthInsurance',
    'citizenHealth.nationality',
    /* EQUIPE DE SAÚDE */
    'teamName',
    'healthTeamType',
    'healthUnitCnes',
    'ine'
  ],
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      { layer: 'trend', degree: 1 },
      { layer: 'calendarSeasonality', type: 'dayOfWeek', degree: 0 },
      { layer: 'calendarSeasonality', type: 'monthOfYear', degree: 0 },
      { layer: 'calendarSeasonality', type: 'holiday', degree: 0 },
      { layer: 'calendarSeasonality', type: 'dayOfMonth', degree: 0 },
      { layer: 'harmonicSeasonality', optimization: { minimumDays: 30, lossFunction: 'gradientMeanSquaredError' } },
      { layer: 'harmonicSeasonality', optimization: { minimumDays: 30, lossFunction: 'gradientMeanSquaredError' } },
    ],
  },
  assertions: prepareAssertions(healthTeamExpression),
}

const healthTeamStatsDefinition = defineAction({
  ...healthTeamStatsAction,
  input: z.object({
    variable,
    highlight: healthTeamExpression.optional(),
    filter: healthTeamExpression.optional(),
    space: spaceForPoint().optional(),
    time: timeForCurrentOnly().optional(),
    select: select(healthTeamShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (
      input.filter ||
      input.space?.delimitationType === 'custom' ||
      (input.time?.type &&
        ['customDateRange', 'customDateRangePredicted'].includes(input.time?.type))
    ) {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})

export default healthTeamStatsDefinition
